/**
 * 资源节点编辑器。
 *
 * 面向设备时突出业务定义（形态、型号及其排产能力），系统字段收纳在高级设置；
 * 其他节点保留层级与归属配置，三种模式共用同一提交协议。
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import {
  WxbCollapse,
  WxbModal,
  WxbInput,
  WxbInputNumber,
  WxbSelect,
  WxbSwitch,
  WxbFormField,
  WxbTextarea,
  WxbTag,
  WxbAlert,
  WxbButton,
} from '../wxb-ui';
import './EquipmentEditModal.css';
import type {
  ResourceNode,
  ResourceNodeClass,
  ResourceNodeScope,
  EquipmentSystemType,
  ResourceNodePayload,
} from '../ProcessTemplateV2/types';
import {
  NODE_CLASS_OPTIONS,
  NODE_CLASS_LABEL,
  NODE_SUBTYPE_OPTIONS,
  NODE_SCOPE_OPTIONS,
  DEPARTMENT_OPTIONS,
  SYSTEM_TYPE_OPTIONS,
  allowedChildBlueprints,
  buildNodeCodePreview,
  isRoomSchedulingResource,
  requiresSubtype,
  supportsOptionalSubtype,
  type NodeBlueprint,
} from './resourceNodeConstants';
import { resourceNodeEquipmentCatalogApi } from '../../services/resourceNodeEquipmentCatalogApi';
import type {
  ResourceNodeEquipmentCatalog,
  ResourceNodeEquipmentModelCapability,
} from '../../types/resourceNodeEquipmentCatalog';

/* ────────────────── Types ────────────────── */

export type FormMode = 'edit' | 'create-root' | 'create-child';

interface NodeFormValues {
  nodeName: string;
  nodeCode: string;
  nodeClass: ResourceNodeClass;
  nodeSubtype: string;
  parentId: number | null;
  nodeScope: ResourceNodeScope;
  departmentCode: string | null;
  equipmentSystemType: EquipmentSystemType | null;
  equipmentCategoryId: number | null;
  equipmentModelId: number | null;
  sortOrder: number | undefined;
  isActive: boolean;
  metadataText: string;
}

interface EquipmentEditModalProps {
  open: boolean;
  embedded?: boolean;
  readOnly?: boolean;
  mode: FormMode;
  editingNode: ResourceNode | null;
  parentNode: ResourceNode | null;
  allNodes: ResourceNode[];
  initialChildClass: ResourceNodeClass | null;
  catalogRevision: number;
  onCancel: () => void;
  onCreate: (payload: ResourceNodePayload) => Promise<any>;
  onUpdate: (nodeId: number, payload: Partial<ResourceNodePayload>) => Promise<void>;
}

const formatCapability = (capability: ResourceNodeEquipmentModelCapability) => {
  if (capability.capacityDimension === 'NONE') return capability.capabilityName;
  const min = capability.minCapacity ?? '—';
  const max = capability.maxCapacity ?? '—';
  return `${capability.capabilityName} ${min}–${max}${capability.canonicalUnit ? ` ${capability.canonicalUnit}` : ''}`;
};

/* ────────────────── Component ────────────────── */

const EquipmentEditModal: React.FC<EquipmentEditModalProps> = ({
  open,
  embedded = false,
  readOnly = false,
  mode,
  editingNode,
  parentNode,
  allNodes,
  initialChildClass,
  catalogRevision,
  onCancel,
  onCreate,
  onUpdate,
}) => {
  const [draft, setDraft] = useState<NodeFormValues>({
    nodeName: '',
    nodeCode: '',
    nodeClass: 'SITE',
    nodeSubtype: '',
    parentId: null,
    nodeScope: 'GLOBAL',
    departmentCode: null,
    equipmentSystemType: null,
    equipmentCategoryId: null,
    equipmentModelId: null,
    sortOrder: undefined,
    isActive: true,
    metadataText: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [equipmentCatalog, setEquipmentCatalog] = useState<ResourceNodeEquipmentCatalog>({
    categories: [],
    models: [],
    capabilityDefinitions: [],
    stateProfiles: [],
  });
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState('');

  /* ── Derive child blueprints ── */
  const childBlueprints = useMemo<NodeBlueprint[]>(
    () => (mode === 'create-child' ? allowedChildBlueprints(parentNode) : []),
    [mode, parentNode],
  );

  /* ── Class options filtered by mode ── */
  const classOptions = useMemo(() => {
    if (mode === 'create-root') {
      return NODE_CLASS_OPTIONS.filter((o) => o.value === 'SITE');
    }
    if (mode === 'create-child') {
      const allowed = new Set(childBlueprints.map((b) => b.nodeClass));
      return NODE_CLASS_OPTIONS.filter((o) => allowed.has(o.value));
    }
    return NODE_CLASS_OPTIONS;
  }, [childBlueprints, mode]);

  /* ── Subtype options ── */
  const subtypeOptions = useMemo(() => {
    if (mode === 'create-child') {
      const fromBlueprints = childBlueprints
        .filter((b) => b.nodeClass === draft.nodeClass && b.nodeSubtype)
        .map((b) => ({ label: b.nodeSubtype!, value: b.nodeSubtype! }));
      if (fromBlueprints.length) return fromBlueprints;
    }
    return NODE_SUBTYPE_OPTIONS[draft.nodeClass] ?? [];
  }, [childBlueprints, draft.nodeClass, mode]);

  /* ── Code preview ── */
  const codePreview = useMemo(() => {
    if (mode === 'edit') return draft.nodeCode;
    return buildNodeCodePreview(draft.nodeScope, draft.departmentCode, draft.nodeClass, allNodes);
  }, [allNodes, draft.departmentCode, draft.nodeClass, draft.nodeCode, draft.nodeScope, mode]);

  /* ── Initialize form on open ── */
  useEffect(() => {
    if (!open) return;

    if (mode === 'edit' && editingNode) {
      setDraft({
        nodeName: editingNode.nodeName,
        nodeCode: editingNode.nodeCode,
        nodeClass: editingNode.nodeClass,
        nodeSubtype: editingNode.nodeSubtype ?? '',
        parentId: editingNode.parentId ?? null,
        nodeScope: editingNode.nodeScope,
        departmentCode: editingNode.departmentCode,
        equipmentSystemType: editingNode.equipmentSystemType ?? null,
        equipmentCategoryId: editingNode.equipmentCategoryId ?? null,
        equipmentModelId: editingNode.equipmentModelId ?? null,
        sortOrder: editingNode.sortOrder,
        isActive: editingNode.isActive,
        metadataText: editingNode.metadata ? JSON.stringify(editingNode.metadata, null, 2) : '',
      });
    } else if (mode === 'create-root') {
      setDraft({
        nodeName: '',
        nodeCode: '',
        nodeClass: 'SITE',
        nodeSubtype: '',
        parentId: null,
        nodeScope: 'GLOBAL',
        departmentCode: null,
        equipmentSystemType: null,
        equipmentCategoryId: null,
        equipmentModelId: null,
        sortOrder: undefined,
        isActive: true,
        metadataText: '',
      });
    } else if (mode === 'create-child' && parentNode) {
      const firstBp = childBlueprints.find((blueprint) => blueprint.nodeClass === initialChildClass) ?? childBlueprints[0];
      setDraft({
        nodeName: '',
        nodeCode: '',
        nodeClass: firstBp?.nodeClass ?? 'EQUIPMENT_UNIT',
        nodeSubtype: firstBp?.nodeSubtype ?? '',
        parentId: parentNode.id,
        nodeScope: parentNode.nodeScope,
        departmentCode: parentNode.departmentCode,
        equipmentSystemType: null,
        equipmentCategoryId: null,
        equipmentModelId: null,
        sortOrder: undefined,
        isActive: true,
        metadataText: '',
      });
    }
  }, [open, mode, editingNode, parentNode, childBlueprints, initialChildClass, readOnly]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCatalogLoading(true);
    setCatalogError('');
    resourceNodeEquipmentCatalogApi.getCatalog()
      .then((result) => {
        if (!cancelled) setEquipmentCatalog(result);
      })
      .catch((error) => {
        if (!cancelled) {
          setCatalogError(error?.response?.data?.error || error?.message || '设备字典加载失败');
        }
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogRevision, open]);

  const equipmentModelOptions = useMemo(
    () => equipmentCatalog.categories
      .map((category) => {
        const models = equipmentCatalog.models
          .filter((item) => item.categoryId === category.id)
          .filter((item) => (
            (category.isActive && item.isActive && item.selectableForNewNodes)
            || item.id === draft.equipmentModelId
          ))
          .map((item) => ({
            value: item.id,
            label: `${item.modelName}${
              category.isActive && item.isActive && item.selectableForNewNodes
                ? ''
                : '（历史关联）'
            }`,
          }));
        return {
          label: category.categoryName,
          options: models,
        };
      })
      .filter((group) => group.options.length > 0),
    [draft.equipmentModelId, equipmentCatalog.categories, equipmentCatalog.models],
  );

  const selectedEquipmentTypeName = useMemo(
    () => equipmentCatalog.categories.find((item) => item.id === draft.equipmentCategoryId)?.categoryName ?? '',
    [draft.equipmentCategoryId, equipmentCatalog.categories],
  );

  const selectedEquipmentModel = useMemo(
    () => equipmentCatalog.models.find((item) => item.id === draft.equipmentModelId) ?? null,
    [draft.equipmentModelId, equipmentCatalog.models],
  );

  const isRoomResource = mode === 'edit' && isRoomSchedulingResource(editingNode);
  const objectLabel = isRoomResource ? '排产资源' : NODE_CLASS_LABEL[draft.nodeClass];
  const parentContext = useMemo(() => {
    if (mode === 'create-child') return parentNode;
    if (mode === 'edit' && editingNode?.parentId) {
      return allNodes.find((item) => item.id === editingNode.parentId) ?? null;
    }
    return null;
  }, [allNodes, editingNode, mode, parentNode]);
  const isEquipment = draft.nodeClass === 'EQUIPMENT_UNIT';

  /* ── Side effects: auto-fix scope/equipment constraints ── */
  useEffect(() => {
    setDraft((prev) => {
      let next = { ...prev };

      // SITE must be GLOBAL
      if (next.nodeClass === 'SITE' && next.nodeScope !== 'GLOBAL') {
        next.nodeScope = 'GLOBAL';
      }

      // GLOBAL => no department
      if (next.nodeScope === 'GLOBAL' && next.departmentCode !== null) {
        next.departmentCode = null;
      }

      // DEPARTMENT => must have department
      if (next.nodeScope === 'DEPARTMENT' && !next.departmentCode) {
        next.departmentCode = 'USP';
      }

      // Non-EQUIPMENT_UNIT => clear equipment fields
      if (next.nodeClass !== 'EQUIPMENT_UNIT') {
        if (next.equipmentSystemType || next.equipmentCategoryId || next.equipmentModelId) {
          next.equipmentSystemType = null;
          next.equipmentCategoryId = null;
          next.equipmentModelId = null;
        }
      }

      return next;
    });
  }, [draft.nodeClass, draft.nodeScope]);

  /* ── Update helper ── */
  const updateField = useCallback(<K extends keyof NodeFormValues>(key: K, value: NodeFormValues[K]) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  }, []);

  /* ── Validate & submit ── */
  const handleOk = async () => {
    if (!draft.nodeName.trim()) {
      message.error(`请输入${objectLabel}名称`);
      return;
    }
    if (requiresSubtype(draft.nodeClass) && !draft.nodeSubtype.trim()) {
      message.error('当前节点类型要求填写子类型');
      return;
    }
    if (draft.nodeScope === 'DEPARTMENT' && !draft.departmentCode) {
      message.error('部门域范围下必须选择部门');
      return;
    }
    if (
      draft.nodeClass === 'EQUIPMENT_UNIT' &&
      !isRoomResource &&
      (!draft.equipmentSystemType || (draft.equipmentSystemType !== 'VIRTUAL' && !draft.equipmentModelId))
    ) {
      message.error(
        draft.equipmentSystemType
          ? '物理设备必须选择设备型号'
          : '请选择设备形态',
      );
      return;
    }
    if (
      draft.nodeClass === 'EQUIPMENT_UNIT'
      && !isRoomResource
      && draft.equipmentSystemType !== 'VIRTUAL'
      && (catalogLoading || catalogError)
    ) {
      message.error(catalogError || '设备字典仍在加载，请稍后再试');
      return;
    }

    let metadata: Record<string, unknown> | null = null;
    if (draft.metadataText?.trim()) {
      try {
        metadata = JSON.parse(draft.metadataText);
      } catch {
        message.error('扩展信息 JSON 格式不正确');
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload: any = {
        nodeName: draft.nodeName.trim(),
        nodeClass: draft.nodeClass,
        nodeSubtype:
          requiresSubtype(draft.nodeClass) || supportsOptionalSubtype(draft.nodeClass)
            ? draft.nodeSubtype.trim().toUpperCase() || null
            : null,
        parentId: draft.parentId ?? null,
        nodeScope: draft.nodeScope,
        departmentCode: draft.nodeScope === 'DEPARTMENT' ? draft.departmentCode : null,
        equipmentSystemType: draft.nodeClass === 'EQUIPMENT_UNIT' ? draft.equipmentSystemType : null,
        equipmentModelId: draft.nodeClass === 'EQUIPMENT_UNIT' ? draft.equipmentModelId : null,
        equipmentClass: null,
        equipmentModel: null,
        sortOrder: draft.sortOrder,
        isActive: draft.isActive,
        metadata,
      };

      if (mode === 'edit' && editingNode) {
        payload.nodeCode = draft.nodeCode.trim();
        await onUpdate(editingNode.id, payload);
        message.success(`${objectLabel}已更新`);
      } else {
        await onCreate(payload);
        message.success(`${objectLabel}已创建`);
      }
      onCancel();
    } catch (err: any) {
      message.error(err?.response?.data?.error || err?.message || '操作失败');
    } finally {
      setSubmitting(false);
    }
  };

  const title = `${mode === 'edit' ? '编辑' : '新增'}${objectLabel}`;

  const codeField = (
    <WxbFormField label="节点编码" helpText={mode === 'edit' ? '用于系统识别和接口引用，请谨慎修改' : undefined}>
      {mode === 'edit' ? (
        <WxbInput
          value={draft.nodeCode}
          onChange={(event) => updateField('nodeCode', event.target.value)}
          disabled={readOnly}
        />
      ) : (
        <div className="equip-code-preview">
          <WxbTag color="blue">{codePreview}</WxbTag>
          <span className="equip-code-hint">创建时自动生成</span>
        </div>
      )}
    </WxbFormField>
  );

  const metadataField = (
    <WxbFormField label="扩展属性（JSON）" helpText="仅用于特殊集成或自定义属性；一般无需填写">
      <WxbTextarea
        value={draft.metadataText}
        onChange={(event) => updateField('metadataText', event.target.value)}
        placeholder='{"key": "value"}'
        rows={4}
        disabled={readOnly}
      />
    </WxbFormField>
  );

  const advancedContent = (
    <div className="equip-edit-advanced-content">
      <div className="equip-edit-form-row">
        {codeField}
        <WxbFormField label="排序权重" helpText="数值越小越靠前">
          <WxbInputNumber
            value={draft.sortOrder}
            onChange={(value) => updateField('sortOrder', typeof value === 'number' ? value : undefined)}
            min={0}
            placeholder="0"
            disabled={readOnly}
          />
        </WxbFormField>
      </div>

      {isEquipment && (
        <div className="equip-edit-form-row">
          <WxbFormField label="节点类型">
            <WxbSelect
              value={draft.nodeClass}
              options={classOptions.map((option) => ({ label: option.label, value: option.value }))}
              disabled
            />
          </WxbFormField>
          <WxbFormField label="管理范围" required>
            <WxbSelect
              value={draft.nodeScope}
              onChange={(value) => updateField('nodeScope', value as ResourceNodeScope)}
              options={NODE_SCOPE_OPTIONS}
              disabled={readOnly}
            />
          </WxbFormField>
        </div>
      )}

      {metadataField}
    </div>
  );

  const formContent = (
    <div className="equip-edit-form">
      {!embedded && (
        <div className="equip-edit-object-summary">
          <div className="equip-edit-object-main">
            <WxbTag color={isEquipment ? 'amber' : 'blue'}>{objectLabel}</WxbTag>
            <div className="equip-edit-object-copy">
              <strong>{draft.nodeName.trim() || `未命名${objectLabel}`}</strong>
              <span>{mode === 'edit' ? draft.nodeCode : codePreview}</span>
            </div>
          </div>
          <div className="equip-edit-object-context">
            <span>{parentContext ? `上级：${parentContext.nodeName}` : '顶层节点'}</span>
            <WxbTag color={draft.isActive ? 'green' : 'neutral'}>
              {draft.isActive ? '启用' : '停用'}
            </WxbTag>
          </div>
        </div>
      )}

        <div className="equip-edit-section">
          <div className="equip-edit-section-heading">
            <div>
              <h3>基本信息</h3>
              <p>{readOnly ? '查看日常识别和管理信息。' : '维护日常识别和管理需要的信息。'}</p>
            </div>
          </div>
          <WxbFormField label={`${objectLabel}名称`} required>
            <WxbInput
              value={draft.nodeName}
              onChange={(event) => updateField('nodeName', event.target.value)}
              placeholder={isEquipment ? '例如：反应釜 BR-201' : `请输入${objectLabel}名称`}
              disabled={readOnly}
            />
          </WxbFormField>
          <div className="equip-edit-form-row">
            {draft.nodeScope === 'DEPARTMENT' ? (
              <WxbFormField label="所属部门" required>
                <WxbSelect
                  value={draft.departmentCode ?? undefined}
                  onChange={(value) => updateField('departmentCode', value ?? null)}
                  options={DEPARTMENT_OPTIONS}
                  placeholder="选择部门"
                  disabled={readOnly}
                />
              </WxbFormField>
            ) : (
              <div className="equip-edit-readonly-field">
                <span>所属范围</span>
                <strong>全局共享</strong>
              </div>
            )}
            <WxbFormField label="启用状态">
              <div className="equip-edit-switch-control">
                <WxbSwitch
                  checked={draft.isActive}
                  onChange={(value) => updateField('isActive', value)}
                  disabled={readOnly}
                />
                <span>{draft.isActive ? '可正常使用' : '暂停使用'}</span>
              </div>
            </WxbFormField>
          </div>
        </div>

        {!isEquipment && (
          <div className="equip-edit-section">
            <div className="equip-edit-section-heading">
              <div>
                <h3>结构与归属</h3>
                <p>{readOnly ? '查看该节点在工厂资源层级中的位置。' : '定义该节点在工厂资源层级中的位置。'}</p>
              </div>
            </div>
            <div className="equip-edit-form-row">
              <WxbFormField label="节点类型" required>
                <WxbSelect
                  value={draft.nodeClass}
                  onChange={(value) => updateField('nodeClass', value as ResourceNodeClass)}
                  options={classOptions.map((option) => ({ label: option.label, value: option.value }))}
                  disabled={mode === 'edit'}
                />
              </WxbFormField>
              {(requiresSubtype(draft.nodeClass) || supportsOptionalSubtype(draft.nodeClass)) ? (
                <WxbFormField label="房间用途" required={requiresSubtype(draft.nodeClass)}>
                  <WxbSelect
                    value={draft.nodeSubtype || undefined}
                    onChange={(value) => updateField('nodeSubtype', value ?? '')}
                    options={subtypeOptions}
                    placeholder="选择用途"
                    allowClear={supportsOptionalSubtype(draft.nodeClass)}
                    disabled={readOnly}
                  />
                </WxbFormField>
              ) : (
                <WxbFormField label="管理范围" required>
                  <WxbSelect
                    value={draft.nodeScope}
                    onChange={(value) => updateField('nodeScope', value as ResourceNodeScope)}
                    options={NODE_SCOPE_OPTIONS}
                    disabled={readOnly || draft.nodeClass === 'SITE'}
                  />
                </WxbFormField>
              )}
            </div>
            {(requiresSubtype(draft.nodeClass) || supportsOptionalSubtype(draft.nodeClass)) && (
              <div className="equip-edit-form-row">
                <WxbFormField label="管理范围" required>
                  <WxbSelect
                    value={draft.nodeScope}
                    onChange={(value) => updateField('nodeScope', value as ResourceNodeScope)}
                    options={NODE_SCOPE_OPTIONS}
                    disabled={readOnly || draft.nodeClass === 'SITE'}
                  />
                </WxbFormField>
                <div className="equip-edit-readonly-field">
                  <span>上级节点</span>
                  <strong>{parentContext?.nodeName ?? '无'}</strong>
                </div>
              </div>
            )}
          </div>
        )}

        {isEquipment && (isRoomResource ? (
          <div className="equip-edit-section equip-edit-equipment-section">
            <div className="equip-edit-section-heading">
              <div>
                <h3>排产资源定义</h3>
                <p>表示所属房间的占用能力，不是需要维护型号的物理设备。</p>
              </div>
              <WxbTag color="blue">房间占用</WxbTag>
            </div>
            <WxbAlert className="equip-alert-compact">
              工序使用此资源时会占用所属房间；房间排产属性由系统自动识别，无需额外维护。
            </WxbAlert>
          </div>
        ) : (
          <div className="equip-edit-section equip-edit-equipment-section">
            <div className="equip-edit-section-heading">
              <div>
                <h3>设备定义</h3>
                <p>型号决定设备类型、可执行能力和状态规则。</p>
              </div>
              <WxbTag color="blue">排产依据</WxbTag>
            </div>

            {catalogError && (
              <WxbAlert variant="error" className="equip-alert-compact">{catalogError}</WxbAlert>
            )}

            <div className="equip-edit-form-row">
              <WxbFormField label="设备形态" required>
                <WxbSelect
                  value={draft.equipmentSystemType ?? undefined}
                  onChange={(value) => {
                    const equipmentSystemType = (value as EquipmentSystemType) ?? null;
                    setDraft((current) => ({
                      ...current,
                      equipmentSystemType,
                      equipmentCategoryId: equipmentSystemType === 'VIRTUAL'
                        ? null
                        : current.equipmentCategoryId,
                      equipmentModelId: equipmentSystemType === 'VIRTUAL'
                        ? null
                        : current.equipmentModelId,
                    }));
                  }}
                  options={SYSTEM_TYPE_OPTIONS}
                  placeholder="一次性 / 不锈钢 / 虚拟"
                  disabled={readOnly}
                />
              </WxbFormField>

              <WxbFormField label="设备型号" required={draft.equipmentSystemType !== 'VIRTUAL'}>
                <WxbSelect
                  value={draft.equipmentModelId ?? undefined}
                  onChange={(value) => {
                    const modelId = value ? Number(value) : null;
                    const model = equipmentCatalog.models.find((item) => item.id === modelId);
                    setDraft((current) => ({
                      ...current,
                      equipmentModelId: modelId,
                      equipmentCategoryId: model?.categoryId ?? null,
                    }));
                  }}
                  options={equipmentModelOptions}
                  placeholder={
                    draft.equipmentSystemType === 'VIRTUAL'
                      ? '虚拟设备无需选择'
                      : catalogLoading
                        ? '加载设备字典...'
                        : '按设备类型选择型号'
                  }
                  loading={catalogLoading}
                  disabled={
                    readOnly
                    || draft.equipmentSystemType === 'VIRTUAL'
                    || catalogLoading
                    || Boolean(catalogError)
                  }
                  allowClear
                  showSearch
                  optionFilterProp="label"
                />
              </WxbFormField>
            </div>

            {draft.equipmentSystemType === 'VIRTUAL' ? (
              <div className="equip-edit-model-empty">
                <WxbTag color="blue">虚拟设备</WxbTag>
                <span>不关联物理型号；具体排产规则由虚拟资源自身配置决定。</span>
              </div>
            ) : selectedEquipmentModel ? (
              <div className="equip-edit-model-profile">
                <div className="equip-edit-profile-item">
                  <span>设备类型</span>
                  <strong>{selectedEquipmentTypeName || selectedEquipmentModel.categoryName}</strong>
                </div>
                <div className="equip-edit-profile-item">
                  <span>型号</span>
                  <strong>{selectedEquipmentModel.modelName}</strong>
                </div>
                <div className="equip-edit-profile-item">
                  <span>状态模型</span>
                  {selectedEquipmentModel.stateProfileName
                    ? <strong>{selectedEquipmentModel.stateProfileName}</strong>
                    : <WxbTag color="amber">未关联</WxbTag>}
                </div>
                <div className="equip-edit-profile-capabilities">
                  <span>可执行能力</span>
                  <div>
                    {selectedEquipmentModel.capabilities.length > 0 ? (
                      selectedEquipmentModel.capabilities.map((capability) => (
                        <WxbTag key={capability.capabilityId} color="blue">
                          {formatCapability(capability)}
                        </WxbTag>
                      ))
                    ) : (
                      <WxbTag color="amber">未配置排产能力</WxbTag>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="equip-edit-model-empty">
                <WxbTag color="amber">待选择</WxbTag>
                <span>选择型号后，可在此核对设备类型、排产能力和状态模型。</span>
              </div>
            )}
          </div>
        ))}

        <WxbCollapse
          className="equip-edit-advanced"
          items={[{
            key: 'advanced',
            label: (
              <div className="equip-edit-advanced-label">
                <span>高级设置</span>
                <span>编码、范围、排序与扩展属性</span>
              </div>
            ),
            children: advancedContent,
          }]}
        />
    </div>
  );

  if (embedded) {
    if (!open) return null;
    return (
      <div className="equip-edit-embedded">
        {formContent}
        {!readOnly && (
          <div className="equip-edit-embedded-actions">
            <WxbButton variant="secondary" onClick={onCancel} disabled={submitting}>
              取消编辑
            </WxbButton>
            <WxbButton onClick={() => void handleOk()} disabled={submitting}>
              {submitting ? '保存中…' : mode === 'edit' ? '保存修改' : '创建'}
            </WxbButton>
          </div>
        )}
      </div>
    );
  }

  return (
    <WxbModal
      open={open}
      title={title}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={submitting}
      okText={mode === 'edit' ? '保存' : '创建'}
      width={720}
      className="equip-edit-modal"
      destroyOnClose
    >
      {formContent}
    </WxbModal>
  );
};

export default EquipmentEditModal;
